<!-- Original copied from https://github.com/xxRockOnxx/vue2-timepicker -->
<template>
  <span class="time-picker">
    <input
      class="display-time"
      :readonly="!disabled"
      :disabled="disabled"
      :value="displayTime"
      @click.stop="toggleDropdown"
      type="text"
    />
    <span
      class="clear-btn"
      v-if="!hideClearButton"
      v-show="!showDropdown && showClearBtn"
      @click.stop="clearTime"
      >&times;</span
    >
    <div
      class="time-picker-overlay"
      v-if="showDropdown"
      @click.stop="toggleDropdown"
    ></div>
    <div class="dropdown" v-show="showDropdown">
      <div class="select-list">
        <ul class="hours">
          <li class="hint" v-text="hourType"></li>
          <li
            v-for="hr in hours"
            :key="hr"
            v-text="hr"
            v-show="disabledValues.hour.indexOf(hr) === -1"
            :class="{ active: value[hourType] === hr }"
            @click.stop="onHourSelect(hr)"
          ></li>
        </ul>
        <ul class="minutes">
          <li class="hint" v-text="minuteType"></li>
          <li
            v-for="m in minutes"
            :key="m"
            v-text="m"
            v-show="disabledValues.minute.indexOf(m) === -1"
            :class="{ active: value[minuteType] === m }"
            @click.stop="onMinuteSelect(m)"
          ></li>
        </ul>
        <ul class="seconds" v-if="secondType">
          <li class="hint" v-text="secondType"></li>
          <li
            v-for="s in seconds"
            :key="s"
            v-text="s"
            v-show="disabledValues.second.indexOf(s) === -1"
            :class="{ active: value[secondType] === s }"
            @click.stop="onSecondSelect(s)"
          ></li>
        </ul>
        <ul class="apms" v-if="apmType">
          <li class="hint" v-text="apmType"></li>
          <li
            v-for="a in apms"
            :key="a"
            v-text="a"
            v-show="disabledValues.apm.indexOf(a) === -1"
            :class="{ active: value[apmType] === a }"
            @click.stop="onApmSelect(a)"
          ></li>
        </ul>
      </div>
    </div>
  </span>
</template>

<script>
const CONFIG = {
  HOUR_TOKENS: ['HH', 'H', 'hh', 'h', 'kk', 'k'],
  MINUTE_TOKENS: ['mm', 'm'],
  SECOND_TOKENS: ['ss', 's'],
  APM_TOKENS: ['A', 'a'],
};

export default {
  name: 'cw-time-picker',

  props: {
    value: {
      required: true,
      type: Object,
    },

    hideClearButton: {
      type: Boolean,
    },

    format: {
      type: String,
      default: 'HH:mm',
    },

    minuteInterval: {
      type: Number,
      default: 1,
      validator: val => {
        if (val < 0) {
          throw new Error('minute-interval must be greater than 0');
        }
        return true;
      },
    },

    secondInterval: {
      type: Number,
      default: 1,
      validator: val => {
        if (val < 0) {
          throw new Error('second-interval must be greater than 0');
        }
        return true;
      },
    },

    disabled: { type: Boolean },

    disabledValues: {
      type: Object,
      default: () => {
        return { hour: [], minute: [], second: [], apm: [] };
      },
    },

    placeholder: {
      type: String,
      default: 'Select Time',
    },
  },

  data() {
    return {
      showDropdown: false,
    };
  },

  computed: {
    displayTime() {
      let formatString = this.format;

      if (this.value[this.hourType]) {
        formatString = formatString.replace(
          new RegExp(this.hourType, 'g'),
          this.value[this.hourType]
        );
      } else {
        return this.placeholder || '';
      }
      if (this.value[this.minuteType]) {
        formatString = formatString.replace(
          new RegExp(this.minuteType, 'g'),
          this.value[this.minuteType]
        );
      }
      if (this.value[this.secondType] && this.secondType) {
        formatString = formatString.replace(
          new RegExp(this.secondType, 'g'),
          this.value[this.secondType]
        );
      }
      if (this.value[this.apmType] && this.apmType) {
        formatString = formatString.replace(
          new RegExp(this.apmType, 'g'),
          this.value[this.apmType]
        );
      }

      return formatString;
    },

    showClearBtn() {
      return !!this.value[this.hourType] || !!this.value[this.minuteType];
    },

    hourType() {
      return this.checkAcceptingType(CONFIG.HOUR_TOKENS, this.format, 'HH');
    },

    minuteType() {
      return this.checkAcceptingType(CONFIG.MINUTE_TOKENS, this.format, 'mm');
    },

    secondType() {
      return this.checkAcceptingType(CONFIG.SECOND_TOKENS, this.format);
    },

    apmType() {
      return this.checkAcceptingType(CONFIG.APM_TOKENS, this.format);
    },

    hours() {
      const hoursCount = this.isTwelveHours ? 12 : 24;

      let hours = [];

      for (let i = 0; i < hoursCount; i++) {
        switch (this.hourType) {
          case 'H':
            hours.push(String(i));
            break;
          case 'HH':
            hours.push(('0' + i).substr(-2, 2));
            break;
          case 'h':
          case 'k':
            hours.push(String(i + 1));
            break;
          case 'hh':
          case 'kk':
            hours.push(('0' + String(i + 1)).substr(-2, 2));
            break;
        }
      }

      return hours;
    },

    minutes() {
      let minutes = [];

      for (let i = 0; i < 60; i += this.minuteInterval) {
        switch (this.minuteType) {
          case 'm':
            minutes.push(String(i));
            break;
          case 'mm':
            minutes.push(('0' + i).substr(-2, 2));
            break;
        }
      }

      return minutes;
    },

    seconds() {
      let seconds = [];

      for (let i = 0; i < 60; i += this.secondInterval) {
        switch (this.secondType) {
          case 's':
            seconds.push(i);
            break;
          case 'ss':
            seconds.push(('0' + i).substr(-2, 2));
            break;
        }
      }

      return seconds;
    },

    apms() {
      switch (this.apmType) {
        case 'A':
          return ['AM', 'PM'];
        case 'a':
          return ['am', 'pm'];
        default:
          return [];
      }
    },

    isTwelveHours() {
      return this.hourType === 'h' || this.hourType === 'hh';
    },

    isPastNoon() {
      return this.apm === 'pm' || this.apm === 'PM';
    },
  },

  methods: {
    checkAcceptingType(validValues, formatString, fallbackValue) {
      if (!validValues || !formatString || !formatString.length) {
        return '';
      }

      const length = validValues.length;

      for (let i = 0; i < length; i++) {
        if (formatString.indexOf(validValues[i]) > -1) {
          return validValues[i];
        }
      }

      return fallbackValue || '';
    },

    toggleDropdown() {
      this.showDropdown = !this.showDropdown && !this.disabled;
    },

    onHourSelect(value) {
      let adjustments = {};
      if (this.value[this.minuteType].length < 1) {
        adjustments[this.minuteType] = this.minutes[0];
      }
      if (
        (!this.value[this.apmType] && this.apms.length > 0) ||
        (this.value[this.apmType].length < 1 && this.apms.length > 0)
      ) {
        adjustments[this.apmType] = this.apms[1];
      }
      this.$emit('input', {
        ...this.value,
        ...adjustments,
        [this.hourType]: value,
      });
    },

    onMinuteSelect(value) {
      let adjustments = {};
      if (this.value[this.hourType].length < 1) {
        adjustments[this.hourType] = this.hours[0];
      }
      if (
        (!this.value[this.apmType] && this.apms.length > 0) ||
        (this.value[this.apmType].length < 1 && this.apms.length > 0)
      ) {
        adjustments[this.apmType] = this.apms[1];
      }
      this.$emit('input', {
        ...this.value,
        ...adjustments,
        [this.minuteType]: value,
      });
    },

    onSecondSelect(value) {
      this.$emit('input', {
        ...this.value,
        [this.secondType]: value,
      });
    },

    onApmSelect(value) {
      this.$emit('input', {
        ...this.value,
        [this.apmType]: value,
      });
    },

    clearTime() {
      let time = {};

      time[this.hourType] = '';
      time[this.minuteType] = '';
      time[this.secondType] = '';

      this.apm = this.apms[0];
      this.$emit('input', time);
    },
  },
};
</script>

<style>
.time-picker {
  display: inline-block;
  position: relative;
  font-size: 1em;
  width: 10em;
  font-family: sans-serif;
  vertical-align: middle;
}

.time-picker * {
  box-sizing: border-box;
}

.time-picker input.display-time {
  border: 0;
  border-bottom: 1px solid #d2d2d2;
  width: 10em;
  height: 2.2em;
  padding: 0.3em 0.5em;
  font-size: 1em;
  cursor: pointer;
}

.time-picker .clear-btn {
  position: absolute;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  top: 0;
  right: 0;
  bottom: 0;
  margin-top: -0.15em;
  z-index: 3;
  font-size: 1.1em;
  line-height: 1em;
  vertical-align: middle;
  width: 1.3em;
  color: #d2d2d2;
  background: rgba(255, 255, 255, 0);
  text-align: center;
  font-style: normal;

  -webkit-transition: color 0.2s;
  transition: color 0.2s;
}

.time-picker .clear-btn:hover {
  color: #797979;
  cursor: pointer;
}

.time-picker .time-picker-overlay {
  z-index: 2;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.time-picker .dropdown {
  position: absolute;
  z-index: 5;
  top: calc(2.2em + 2px);
  left: 0;
  background: #fff;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.15);
  width: 10em;
  height: 10em;
  font-weight: normal;
}

.time-picker .dropdown .select-list {
  width: 10em;
  height: 10em;
  overflow: hidden;
  display: flex;
  flex-flow: row nowrap;
  align-items: stretch;
  justify-content: space-between;
}

.time-picker .dropdown ul {
  padding: 0;
  margin: 0;
  list-style: none;

  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
}

.time-picker .dropdown ul.minutes,
.time-picker .dropdown ul.seconds,
.time-picker .dropdown ul.apms {
  border-left: 1px solid #fff;
}

.time-picker .dropdown ul li {
  text-align: center;
  padding: 0.3em 0;
  color: #161616;
}

.time-picker .dropdown ul li:not(.hint):hover {
  background: rgba(0, 0, 0, 0.08);
  color: #161616;
  cursor: pointer;
}

.time-picker .dropdown ul li.active,
.time-picker .dropdown ul li.active:hover {
  background: #b1b0b1;
  color: #fff;
}

.time-picker .dropdown .hint {
  color: #a5a5a5;
  cursor: default;
  font-size: 0.8em;
}
</style>
